import React, { useEffect, useMemo, useState } from "react";
import { Modal, Select, Typography, Divider, Alert, Button } from "antd";
import { PRIORITIES, STATUSES } from "../models/incidents";
import {
  getAllowedNextStatuses,
  normalizeReportedUndefinedPair,
} from "../utils/incidents.helpers";
import { useMediaQuery } from "../../../../utils/useMediaQuery";
import { UI_TIMESTAMP_FORMAT } from "../../../../utils/date.helpers";

import { BREAKPOINTS } from "../../../../utils/breakpoints";

import { useTranslation } from "react-i18next";

const { Text } = Typography;

const priorityOptions = [
  { value: PRIORITIES.HIGH, label: "HIGH" },
  { value: PRIORITIES.MEDIUM, label: "MEDIUM" },
  { value: PRIORITIES.LOW, label: "LOW" },
  { value: PRIORITIES.UNDEFINED, label: "UNDEFINED" },
];

const ALL_STATUSES = [
  STATUSES.REPORTED,
  STATUSES.CONFIRMED,
  STATUSES.SCHEDULED,
  STATUSES.RESOLVED,
  STATUSES.REJECTED,
];

export default function IncidentPopupModal({
  open,
  incident,
  onClose,
  onSave,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [photoUnavailable, setPhotoUnavailable] = useState(false);
  const isLaptop = useMediaQuery(BREAKPOINTS.laptop);

  useEffect(() => {
    if (!incident) return;
    setDraft({ ...incident });
    setError("");
    setPhotoUnavailable(false);
  }, [incident]);

  const isReported = draft?.status === STATUSES.REPORTED;

  const allowedStatusTargets = useMemo(() => {
    if (!incident) return [];
    const next = getAllowedNextStatuses(incident.status);
    return [incident.status, ...next];
  }, [incident]);

  const statusOptions = useMemo(() => {
    if (!draft) return [];
    const allowedSet = new Set(allowedStatusTargets);

    return ALL_STATUSES.map((s) => ({
      value: s,
      label: s,
      disabled: !allowedSet.has(s),
    }));
  }, [draft, allowedStatusTargets]);

  const handleChangeStatus = (newStatus) => {
    setError("");

    setDraft((prev) => {
      if (!prev) return prev;

      if (newStatus === STATUSES.REPORTED) {
        return {
          ...prev,
          status: STATUSES.REPORTED,
          priority: PRIORITIES.UNDEFINED,
        };
      }

      return { ...prev, status: newStatus };
    });

    if (
      newStatus === STATUSES.CONFIRMED &&
      (draft?.priority ?? PRIORITIES.UNDEFINED) === PRIORITIES.UNDEFINED
    ) {
      setError(t("incident.errors.selectPriority"));
    }
  };

  const handleChangePriority = (newPriority) => {
    setError("");
    setDraft((prev) =>
      normalizeReportedUndefinedPair({ ...prev, priority: newPriority })
    );
  };

  const validate = () => {
    if (!draft) return false;

    if (
      draft.status === STATUSES.REPORTED &&
      draft.priority !== PRIORITIES.UNDEFINED
    ) {
      setError(t("incident.errors.priorityRequired"));
      return false;
    }
    if (
      draft.priority === PRIORITIES.UNDEFINED &&
      draft.status !== STATUSES.REPORTED
    ) {
      setError(t("incident.errors.priorityRequired"));
      return false;
    }

    if (
      incident.status === STATUSES.REPORTED &&
      draft.status === STATUSES.CONFIRMED
    ) {
      if (draft.priority === PRIORITIES.UNDEFINED) {
        setError(t("incident.errors.selectPriority"));
        return false;
      }
    }

    if (
      [
        STATUSES.CONFIRMED,
        STATUSES.SCHEDULED,
        STATUSES.RESOLVED,
        STATUSES.REJECTED,
      ].includes(draft.status) &&
      draft.priority === PRIORITIES.UNDEFINED
    ) {
      setError(t("incident.errors.undefinedNotAllowed"));
      return false;
    }

    return true;
  };

  const handleSave = () => {
    setError("");
    if (!validate()) return;

    onSave(draft);
    onClose();
  };

  if (!open || !incident || !draft) return null;

  return (
    <Modal
      open={open}
      title={t("incident.title", { id: incident.id })}
      onCancel={onClose}
      style={{ top: isLaptop ? 32 : 100 }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t("incident.actions.close")}
        </Button>,
        <Button key="save" type="primary" onClick={handleSave}>
          {t("incident.actions.save")}
        </Button>,
      ]}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("incident.coordinates")}
          </Text>
          <div style={{ fontSize: 13 }}>
            {incident.latitude.toFixed(6)}, {incident.longitude.toFixed(6)}
          </div>
        </div>

        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("incident.timestamp", { format: UI_TIMESTAMP_FORMAT })}
          </Text>
          <div style={{ fontSize: 13 }}>{incident.timestamp}</div>
        </div>

        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("incident.reporter")}
          </Text>
          <div style={{ fontSize: 13, textTransform: "capitalize" }}>
            {incident.reporter}
          </div>
        </div>

        {incident.description ? (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("incident.details")}
            </Text>
            <div style={{ fontSize: 13 }}>{incident.description}</div>
          </div>
        ) : null}

        {incident.segmentId ? (
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("incident.segment")}
            </Text>
            <div style={{ fontSize: 13 }}>{incident.segmentId}</div>
          </div>
        ) : null}

        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("incident.photo")}
          </Text>
          <div
            style={{
              marginTop: 6,
              width: "100%",
              height: 140,
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#f9fafb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              color: "#6b7280",
              overflow: "hidden",
            }}
          >
            {incident.photoUrl && !photoUnavailable ? (
              <img
                alt="incident"
                src={incident.photoUrl}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={() => setPhotoUnavailable(true)}
              />
            ) : (
              t("incident.noPhoto")
            )}
          </div>
        </div>

        <Divider style={{ margin: "6px 0" }} />
        <Alert
          type="info"
          showIcon
          message={
            <Text style={{ fontSize: 12 }}>
              <strong>{t("incident.statusInfo.title")}</strong>{" "}
              {t("incident.statusInfo.description")}
              <br />
              <strong>{t("incident.statusInfo.sequence")}</strong>
            </Text>
          }
          style={{
            padding: "6px 10px",
            marginBottom: 10,
          }}
        />
        <div>
          <Text style={{ fontSize: 12, fontWeight: 600 }}>
            {" "}
            {t("incident.priority")}
          </Text>
          <Select
            style={{ width: "100%", marginTop: 6 }}
            value={draft.priority}
            onChange={handleChangePriority}
            options={priorityOptions.map((opt) => ({
              ...opt,
              disabled:
                opt.value === PRIORITIES.UNDEFINED &&
                draft.status !== STATUSES.REPORTED,
            }))}
            disabled={isReported}
          />
          {isReported && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("incident.reportedInfo")}
            </Text>
          )}
        </div>

        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text style={{ fontSize: 12, fontWeight: 600 }}>
              {" "}
              {t("incident.status")}
            </Text>
          </div>

          <Select
            style={{ width: "100%", marginTop: 6 }}
            value={draft.status}
            onChange={handleChangeStatus}
            options={statusOptions}
          />
        </div>

        {error && <Alert type="error" showIcon message={error} />}
      </div>
    </Modal>
  );
}
